// Create file: src/features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/add-contacts/add-contacts-from-campaign/AddContactsFromCampaign.tsx

import { Stack, Typography } from '@mui/material';
import { addContactsFromCampaign, getAllFunnels } from 'API/linkedin-funnel';
import { Funnel, FunnelTypes } from 'features/co-pilot/co-pilot.types';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiSuccessAlert from 'features/ui/base-components/pai-success-alert';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiRadioGroup from 'features/ui/base-components/pai-radio-group';
import PaiSelect from 'features/ui/base-components/pai-select';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useErrorStore from 'stores/error-store';
import { makeStyles } from 'tss-react/mui';

enum ProfileDegree {
  FIRST = 'first',
  SECOND_PLUS = 'secondPlus',
  ALL = 'all',
}

enum ReplyStatus {
  HAS_REACTED = 'hasReacted',
  NO_REACTION = 'noReaction',
  HAS_REPLIED = 'hasReplied',
  NO_REPLY = 'noReply',
  ALL = 'all',
}

enum ReplySentiment {
  POS = 'pos',
  NEG = 'neg',
}

enum IsActiveStatus {
  ALL = 'all',
  IS_ACTIVE = 'isActive',
  NOT_ACTIVE = 'notActive',
}

interface Props {
  targetFunnelId: string;
  targetFunnelType: FunnelTypes;
}

export interface AddContactsFromFunnelRequest {
  sourceFunnelId: string;
  profileDegree?: ProfileDegree;
  replySentiment?: ReplySentiment[];
  replyStatus?: ReplyStatus;
  isActiveStatus?: IsActiveStatus;
}

const AddContactsFromCampaign: React.FC<Props> = ({ targetFunnelId, targetFunnelType }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { token } = useUserData();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [availableFunnels, setAvailableFunnels] = useState<Funnel[]>([]);

  const [sourceFunnelId, setSourceFunnelId] = useState<string>('');
  const [profileDegree, setProfileDegree] = useState<ProfileDegree>(ProfileDegree.ALL);
  const [contactsThatReplied, setContactsThatReplied] = useState<ReplySentiment[]>([]);
  const [contactsThatRepliedFilter, setContactsThatRepliedFilter] = useState<ReplyStatus>(ReplyStatus.ALL);
  const [isActiveFilter, setIsActiveFilter] = useState<IsActiveStatus>(IsActiveStatus.ALL);

  const [isLoadingFunnels, setIsLoadingFunnels] = useState(false);
  const [isAddingContacts, setIsAddingContacts] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successSnackbar, setSuccessSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  useEffect(() => {
    const fetchAvailableFunnels = async () => {
      setIsLoadingFunnels(true);
      setError(null);

      const res = await getAllFunnels(targetFunnelType, undefined, undefined, undefined, undefined, undefined, undefined, true);

      if (!res.error) {
        const resFunnels: Funnel[] = res.campaigns || [];
        // Filter out campaigns with status 'created' and the current campaign
        const filteredFunnels = resFunnels.filter((funnel) => funnel._id !== targetFunnelId);
        setAvailableFunnels(filteredFunnels);
      } else {
        setError(t('addContactsFromCampaignErrorsFetchFunnelsError'));
        handleOpenErrorDialog(res?.error, '/campaigns', 'get');
      }

      setIsLoadingFunnels(false);
    };

    fetchAvailableFunnels();
  }, [targetFunnelId, targetFunnelType, token]);

  const handleSourceFunnelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSourceFunnelId(event.target.value);
    setError(null);
  };

  const handleProfileDegreeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileDegree(event.target.value as ProfileDegree);
    setError(null);
  };

  const handleReplyCheckboxChange = (replyType: ReplySentiment, checked: boolean) => {
    setContactsThatReplied((prev) => {
      if (checked) {
        return [...prev, replyType];
      } else {
        return prev.filter((r) => r !== replyType);
      }
    });

    setError(null);
  };

  const handleReplyStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setContactsThatRepliedFilter(event.target.value as ReplyStatus);
    setError(null);

    if (event.target.value === ReplyStatus.ALL || event.target.value === ReplyStatus.NO_REACTION || event.target.value === ReplyStatus.NO_REPLY) {
      setContactsThatReplied([]);
    }
  };

  const handleIsActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsActiveFilter(event.target.value as IsActiveStatus);
    setError(null);
  };

  const resetForm = () => {
    setSourceFunnelId('');
    setProfileDegree(ProfileDegree.ALL);
    setContactsThatReplied([]);
    setContactsThatRepliedFilter(ReplyStatus.ALL);
    setIsActiveFilter(IsActiveStatus.ALL);
  };

  const handleAddContactsFromCampaign = async () => {
    if (!sourceFunnelId) {
      setError(t('addContactsFromCampaignErrorsNoSourceSelected'));
      return;
    }

    setIsAddingContacts(true);
    setError(null);
    handleCloseSuccessSnackbar();

    const payload: AddContactsFromFunnelRequest = {
      sourceFunnelId,
      profileDegree,
      replyStatus: contactsThatRepliedFilter,
      isActiveStatus: isActiveFilter,
    };

    // Only include contactsThatReplied if it's not empty
    if (contactsThatReplied.length > 0) {
      payload.replySentiment = contactsThatReplied;
    }

    const res = await addContactsFromCampaign(targetFunnelId, payload);

    if (!res.error) {
      handleOpenSuccessSnackbar(!_.isNil(res.addedContacts) ? t('addContactsFromCampaignSuccessMessage', { count: res.addedContacts }) : res.message);
      resetForm();
    } else {
      const errorMsg = res.error?.message || '';
      if (errorMsg.includes('already added')) {
        setError(t('addContactsFromCampaignErrorsAlreadyAddedError'));
      } else {
        setError(t('addContactsFromCampaignErrorsGenericAddError'));
      }
      handleOpenErrorDialog(res?.error, `/campaigns/${targetFunnelId}/add-contacts-from-campaign`, 'post');
    }

    setIsAddingContacts(false);
  };

  const handleOpenSuccessSnackbar = (message: string) => {
    setSuccessSnackbar({ open: true, message });
  };

  const handleCloseSuccessSnackbar = () => {
    setSuccessSnackbar({ open: false, message: '' });
  };

  return (
    <>
      <section className={classes.section}>
        <Typography className={cx(classes.subTitle, 'dense')} mb={'8px'}>
          {t('addContactsFromCampaignSourceFunnelLabel')}
        </Typography>
        <Typography className={classes.greyText} mb={'16px'}>
          {t('addContactsFromCampaignDescription')}
        </Typography>
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <PaiSelect
            value={sourceFunnelId}
            onChange={handleSourceFunnelChange}
            disabled={isLoadingFunnels || isAddingContacts}
            style={{ maxWidth: '350px' }}
          >
            <PaiMenuItem value="" hidden={sourceFunnelId !== ''}>
              {t('addContactsFromCampaignSourceFunnelPlaceholder')}
            </PaiMenuItem>
            {availableFunnels.map((funnel) => (
              <PaiMenuItem key={funnel._id} value={funnel._id}>
                {funnel.name}
              </PaiMenuItem>
            ))}
          </PaiSelect>
          {isLoadingFunnels ? <PaiDotLoader size={'small'} inline={true} /> : null}
        </Stack>
        {error && <Typography className={classes.error}>{error}</Typography>}
        {availableFunnels.length === 0 && !isLoadingFunnels && (
          <Typography className={classes.hint}>{t('addContactsFromCampaignNoOtherFunnels')}</Typography>
        )}
      </section>

      <section className={classes.section}>
        <Typography className={cx(classes.subTitle, 'dense')} mb={'8px'}>
          {t('addContactsFromCampaignIsActiveLabel')}
        </Typography>
        <Typography className={classes.greyText} mb={'16px'}>
          {t('addContactsFromCampaignIsActiveDescription')}
        </Typography>
        <PaiRadioGroup
          value={isActiveFilter}
          items={[
            { label: t('noFilter'), value: IsActiveStatus.ALL },
            { label: t('addContactsFromCampaignIsActiveYes'), value: IsActiveStatus.IS_ACTIVE },
            { label: t('addContactsFromCampaignIsActiveNo'), value: IsActiveStatus.NOT_ACTIVE },
          ]}
          row={true}
          onChange={handleIsActiveChange}
        />
      </section>

      <section className={classes.section}>
        <Typography className={cx(classes.subTitle, 'dense')} mb={'8px'}>
          {t('addContactsFromCampaignContactDegreeLabel')}
        </Typography>
        <Typography className={classes.greyText} mb={'16px'}>
          {t('addContactsFromCampaignContactDegreeDescription')}
        </Typography>
        <PaiRadioGroup
          value={profileDegree}
          items={[
            { label: t('noFilter'), value: ProfileDegree.ALL },
            { label: t('addContactsFromCampaignContactDegreeFirst'), value: ProfileDegree.FIRST },
            { label: t('addContactsFromCampaignContactDegreeSecondPlus'), value: ProfileDegree.SECOND_PLUS },
          ]}
          row={true}
          onChange={handleProfileDegreeChange}
        />
      </section>

      <section className={cx(classes.section, 'border-bottom-disabled')}>
        <Typography className={cx(classes.subTitle, 'dense')} mb={'8px'}>
          {t('addContactsFromCampaignContactsThatRepliedLabel')}
        </Typography>
        <Typography className={classes.greyText} mb={'16px'}>
          {t('addContactsFromCampaignContactsThatRepliedDescription')}
        </Typography>
        <Stack direction={'column'} gap={2}>
          <PaiRadioGroup
            value={contactsThatRepliedFilter}
            items={[
              { label: t('noFilter'), value: ReplyStatus.ALL },
              { label: t('addContactsFromCampaignHasReacted'), value: ReplyStatus.HAS_REACTED },
              { label: t('addContactsFromCampaignHasReplied'), value: ReplyStatus.HAS_REPLIED },
              { label: t('addContactsFromCampaignHasNotReacted'), value: ReplyStatus.NO_REACTION },
              { label: t('addContactsFromCampaignHasNotReplied'), value: ReplyStatus.NO_REPLY },
            ]}
            row={true}
            onChange={handleReplyStatusChange}
          />
          <div className={classes.checkboxGroup}>
            <PaiCheckbox
              label={t('addContactsFromCampaignRepliedPositive')}
              checked={contactsThatReplied.includes(ReplySentiment.POS)}
              onChange={(e) => handleReplyCheckboxChange(ReplySentiment.POS, e.target.checked)}
              disabled={
                isAddingContacts ||
                contactsThatRepliedFilter === ReplyStatus.ALL ||
                contactsThatRepliedFilter === ReplyStatus.NO_REACTION ||
                contactsThatRepliedFilter === ReplyStatus.NO_REPLY
              }
            />
            <PaiCheckbox
              label={t('addContactsFromCampaignRepliedNegative')}
              checked={contactsThatReplied.includes(ReplySentiment.NEG)}
              onChange={(e) => handleReplyCheckboxChange(ReplySentiment.NEG, e.target.checked)}
              disabled={
                isAddingContacts ||
                contactsThatRepliedFilter === ReplyStatus.ALL ||
                contactsThatRepliedFilter === ReplyStatus.NO_REACTION ||
                contactsThatRepliedFilter === ReplyStatus.NO_REPLY
              }
            />
          </div>
        </Stack>
        <Typography className={classes.hint}>{t('addContactsFromCampaignRepliedNote')}</Typography>
      </section>

      <section className={cx(classes.section, 'last-section')}>
        <Stack direction={'row'}>
          <PaiButton
            text={t('addContactsFromCampaignAddButton')}
            loading={isAddingContacts}
            action={handleAddContactsFromCampaign}
            border={'rounded'}
            size={'large'}
            disabled={isLoadingFunnels || isAddingContacts || !sourceFunnelId || availableFunnels.length === 0}
          />
        </Stack>
        <Typography className={classes.hint}>{t('addContactsFromCampaignImportantNote')}</Typography>
      </section>

      <PaiSuccessAlert open={successSnackbar.open} message={successSnackbar.message} position={'bottom'} handleClose={handleCloseSuccessSnackbar} />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  section: {
    position: 'relative',
    padding: '24px 48px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
    '&.border-bottom-disabled': {
      borderBottom: 'none',
    },
    '&.last-section': {
      margin: '0 -48px',
      padding: '24px calc(48px + 48px)',
      borderTop: `1px solid ${theme.palette.greys.lighter}`,
      borderBottom: 'none',
    },
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  checkboxGroup: {
    display: 'flex',
    gap: '16px',
  },
  error: {
    color: theme.palette.error.main,
    fontSize: theme.typography.pxToRem(14),
    marginTop: '16px',
  },
  success: {
    color: theme.palette.success.main,
    fontSize: theme.typography.pxToRem(14),
    marginTop: '16px',
  },
  hint: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.greys.medium,
    marginTop: '16px',
  },
}));

export default AddContactsFromCampaign;
